import { LobbyTable } from 'types/game';
import { tableSeatStatus } from 'lib/tableStatus';

export type QuickJoinMode = 'rejoin_active' | 'last_played' | 'cheapest';

export interface QuickJoinTarget {
  table: LobbyTable;
  mode: QuickJoinMode;
  /** Skip buy-in modal — player already has chips at this table */
  skipBuyIn?: boolean;
}

function tableMinBuyIn(table: LobbyTable): number {
  const explicit = Number(table.min_buy_in);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return Number(table.boot_amount) || 0;
}

function isAffordableNewSeat(table: LobbyTable, freeBalance: number): boolean {
  return tableMinBuyIn(table) <= freeBalance;
}

function findTableById(tables: LobbyTable[], tableProfileId: number): LobbyTable | undefined {
  const id = Math.floor(Number(tableProfileId));
  return tables.find((t) => t.id === id);
}

function pickCheapestAffordable(tables: LobbyTable[], freeBalance: number): LobbyTable | null {
  const candidates = tables
    .filter((t) => tableSeatStatus(t) !== 'full')
    .filter((t) => isAffordableNewSeat(t, freeBalance))
    .sort((a, b) => Number(a.boot_amount) - Number(b.boot_amount));
  return candidates[0] ?? null;
}

/**
 * Quick join priority:
 * 1. Rejoin table where player still has chips
 * 2. Last played table (if affordable with open seat)
 * 3. Cheapest affordable table with open seat
 */
export function pickQuickJoinTarget(
  tables: LobbyTable[],
  freeBalance: number,
  opts: {
    activeTableProfileId?: number | null;
    activeTableStack?: number | null;
    lastPlayedTableProfileId?: number | null;
  }
): QuickJoinTarget | null {
  const activeId = opts.activeTableProfileId
    ? Math.floor(Number(opts.activeTableProfileId))
    : null;
  const activeStack = Math.max(0, Math.floor(Number(opts.activeTableStack) || 0));
  if (activeId && activeId > 0 && activeStack > 0) {
    const active = findTableById(tables, activeId);
    if (active) {
      return { table: active, mode: 'rejoin_active', skipBuyIn: true };
    }
  }

  const lastId = opts.lastPlayedTableProfileId
    ? Math.floor(Number(opts.lastPlayedTableProfileId))
    : null;
  if (lastId && lastId > 0 && lastId !== activeId) {
    const last = findTableById(tables, lastId);
    if (last && tableSeatStatus(last) !== 'full' && isAffordableNewSeat(last, freeBalance)) {
      return { table: last, mode: 'last_played' };
    }
  }

  const cheapest = pickCheapestAffordable(tables, freeBalance);
  if (cheapest) {
    return { table: cheapest, mode: 'cheapest' };
  }

  return null;
}

export function quickJoinTargetLabel(target: QuickJoinTarget): string {
  switch (target.mode) {
    case 'rejoin_active':
      return `Rejoin ${target.table.name}`;
    case 'last_played':
      return `Return to ${target.table.name}`;
    default:
      return `Join ${target.table.name}`;
  }
}
